import React from "react";

import classnames from "classnames";
import { Link } from "react-router-dom";
import { withStyleProps } from "Utils/styleProps";

import BodyText from "Common/BodyText";
import Icon from "Common/Icon";

import makeBem from "Utils/makeBem";

const bem = makeBem("VerticalBreadcrumbs");
interface Crumb {
  title: string;
  description: string;
  status: "complete" | "current" | "incomplete";
  link: string;
}
type Props = {
  crumbs: Crumb[];
  component?: React.ElementType;
  linkPropName?: string;
  onCrumbClick?: (crumb: Crumb, index: number) => void;
};

/**
 * VerticalBreadcrumbs is a component that displays a breadcrumb trail
 * @param {Crumb[]} crumbs - an array of Crumb objects
 * @returns a VerticalBreadcrumbs component
 */
let VerticalBreadcrumbs = ({ crumbs, component = Link, linkPropName = "to", onCrumbClick }: Props) => {
  const options = crumbs.map((crumb, index) => {
    const { status } = crumb;
    const isLast = index === crumbs.length - 1;
    const isIncomplete = status !== "complete";
    let icon = "check-circle";
    let size = "2x";

    if (status === "incomplete") {
      icon = "circle";
      size = "md";
    }

    const children = (
      <>
        <div
          className={classnames(
            bem("markerwrapper"),
            {
              [bem("markerwrapper", "last")]: isLast,
            },
            {
              [bem("markerwrapper", "incomplete")]: isIncomplete,
            },
          )}
        >
          <div className={classnames(bem("marker"), bem("marker", status), "text-center")}>
            {status !== "current" ? <Icon icon={icon} size={size} flavor="solid" /> : <span className={bem("currentMarkerIcon")} />}
          </div>
        </div>
        <div className={bem("label")}>
          <BodyText className={bem("title")}>{crumb.title}</BodyText>
          {crumb.description && <BodyText className={bem("description")}>{crumb.description}</BodyText>}
        </div>
      </>
    );
    const compponentClass = isIncomplete ? "div" : component;
    return React.createElement(
      compponentClass,
      {
        className: bem("breadcrumb"),
        key: `breadcrumb-${index}`,
        [linkPropName]: crumb.link,
        onClick: () => onCrumbClick?.(crumb, index),
      },
      children,
    );
  });
  return <div className={bem("wrapper")}>{options}</div>;
};

VerticalBreadcrumbs = withStyleProps(VerticalBreadcrumbs);
export { VerticalBreadcrumbs };
